<template>

    <kt-modal 
        :visible="recordVisible" 
        :title="recordTitle"
        :size="'modal-xl'" 
        @dismiss="onDismiss()"
    >
        <template #body>
            <pm-form 
                v-model="record"
            ></pm-form>
        </template>

        <template #footer>
            <div class="row w-100">
                <div :class="{ 'col-4': record.Id, 'col-6': !record.Id }">
                    <button @click="onDismiss()" class="btn btn-sm btn-outline-secondary form-control">Close</button>
                </div>
                <div class="col-4" v-if="record.Id">
                    <button @click="deleteRecord()" class="btn btn-sm btn-outline-danger form-control">Delete</button>
                </div>
                <div :class="{ 'col-4': record.Id, 'col-6': !record.Id }">
                    <button @click="saveRecord()" class="btn btn-sm btn-outline-primary form-control">Save</button>
                </div>
            </div>
        </template>
    </kt-modal>

</template>

<script lang="ts" setup>

import { ref, watch, onUnmounted } from "vue";
import { useRoute, useRouter } from "vue-router";

import Emitter from "@/services/Emitter";
import ApiService from "@/services/ApiService";
import { PMModel } from "@/models/PMModel";

import Alert from "@/services/Alert";
import Helper from "@/services/Helper";
import PmForm from "@/components/PMs/PMForm.vue";
import KtModal from "@/components/UI/KtModal.vue";

const route = useRoute();
const router = useRouter();
const recordTitle = ref("PM Entry");
const recordVisible = ref(true);
const unwatch: Array<Function> = [];

const emit = defineEmits(['close']);

const newRecord = (): PMModel => 
{
    const pm = new PMModel();
    pm.IsActive = true;
    pm.FrequencyType = "Monthly";
    return pm;
};

const record = ref<PMModel>(newRecord());

const saveRecord = async () => {

    let data = Helper.prep(record.value);

    // Validate the record
    if (!data.Title)
    {
        Alert.notify({
            text: "Please enter a PM title."
        });
        return;
    }

    if (!data.FrequencyType)
    {
        Alert.notify({
            text: "Please select a frequency type."
        });
        return;
    }

    console.debug("save PM", data);
    
    data = await ApiService.savePM(data);
    
    // Update the record with saved data (includes LinkedRequestId)
    record.value = data;
    
    // Check if we're in a route-based context or global modal
    if (route.name && route.name.toString().includes('PM')) {
        // If this was a new PM, redirect to edit with the new ID
        if (!route.params.id || route.params.id === 'new') {
            Emitter.emit("redirect", "/pm/edit/" + data.Id);
        } else {
            Alert.notify({
                text: "PM saved successfully!",
                type: "success"
            });
        }
    } else {
        // Global modal - close after save
        Alert.notify({
            text: "PM saved successfully!",
            type: "success"
        });
        emit('close');
    }
    
};

const deleteRecord = async () => {
    Alert.confirm("Are you sure you want to delete this PM?", async () => {
        await ApiService.deletePM(record.value.Id!);
        
        // Check if we're in a route-based context or global modal
        if (route.name && route.name.toString().includes('PM')) {
            router.back();
        } else {
            emit('close');
        }
    });
};


const onDismiss = () => {
    // Check if we're in a route-based context or global modal
    if (route.name && route.name.toString().includes('PM')) {
        router.back();
    } else {
        // Global modal - emit close event
        emit('close');
    }
};

const loadRecords = async (val: string | undefined) =>
{
    // Don't load if there's no ID parameter
    if (!val) {
        return;
    }
    
    switch (val) {
        case "new":
            record.value = newRecord();
            recordTitle.value = "New PM Entry";
            break;

        default:
            let rec = await ApiService.loadPM(val);
            console.debug("Loaded PM:", rec);
            record.value = rec;
            recordTitle.value = `Edit PM: ${rec.Title}`;
            break;
    }
};


unwatch.push(watch(() => route.params.id, (nv, ov) => {
    loadRecords(nv as string | undefined);
}, { immediate: true }));

onUnmounted(() =>
{
    unwatch.forEach((rw) => {
        rw();
    });
})

</script>
